import { useState } from 'react'
import { Modal } from './Modal'
import { principalClassLabel } from '../theme/vocabulary'
import type { EnvironmentCredential } from '../api/types'
import { Notice } from './primitives/Notice'

// Pre-delete confirmation (ATC-142).
//
// A THIRD SIBLING of ConfirmRotateModal, after ConfirmAdjustmentModal. Same
// reason the second one was a sibling: `FD-ADR-038` §1.11 promoted
// ConfirmRotateModal into shared suite Tier 1 FROM ATC, naming that exact
// dialog as the standard the other products must match. Extracting a shared
// base would change the thing the suite just standardised on, to save a few
// lines.
//
// WHERE IT DELIBERATELY DIVERGES FROM ITS SIBLINGS: the gate is TYPING THE
// ENVIRONMENT LABEL, not an acknowledgement checkbox.
//
// The two defend against different mistakes, and only one of them is the
// mistake here. A checkbox defends against "I did not realise this was
// destructive" — which is the rotation hazard, where the operator knows which
// environment they are on and is choosing whether to re-key it. Deletion's
// hazard is "I am on the wrong environment": the operator wants to delete
// SOMETHING, and the question is whether it is this one. A checkbox cannot
// tell those apart; typing the label can only be satisfied by reading the
// name of the thing actually about to be destroyed.
//
// It also earns a stronger gate than either sibling by being the only
// genuinely unrecoverable one. Rotation preserves the environment. An
// adjustment is reversible by another grant. A deleted environment cannot be
// re-created: re-issuing mints a NEW server-minted customerEnvironmentId, so
// every device MSI argument and every Manager app setting in the fleet is now
// wrong.
export function ConfirmDeleteModal({
  customerEnvironmentId,
  environmentLabel,
  credentials,
  deviceCount,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  customerEnvironmentId: string
  environmentLabel: string
  /** Every credential that will be destroyed, named by class. */
  credentials: EnvironmentCredential[]
  /**
   * MINT device registrations for this environment, or `null` when the device
   * list could not be read. `null` is NOT 0 — see the notice below.
   */
  deviceCount: number | null
  busy: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
}) {
  const [typed, setTyped] = useState('')

  // Exact match, not trimmed-and-lowercased. A gate that accepts an
  // approximation of the label is a gate that accepts the neighbouring
  // environment's label too — the labels are generated adjective-noun pairs
  // (ATC-140) and several will share a word.
  const confirmed = typed === environmentLabel

  return (
    <Modal title="Delete this environment?" onClose={onCancel} dismissal="deliberate">
      {/* Static impact copy rendered on open, so deliberately a plain div and
          not `Notice tone="error"` — that carries role="alert", which most
          screen readers announce on mount and would interrupt the dialog's own
          title. The dynamic error below IS a Notice, which is what an alert is
          for. Same split as ConfirmRotateModal. */}
      <div className="notice notice-error">
        This <strong>permanently destroys</strong> the environment and every credential it
        holds. It cannot be undone, and it cannot be recreated: re-issuing mints a{' '}
        <strong>new environment id</strong>, so every device and app setting referring to this
        one would be wrong.
      </div>

      <p>
        Deleting <strong>{environmentLabel || 'this environment'}</strong>{' '}
        (<code className="cei">{customerEnvironmentId}</code>) removes:
      </p>
      <ul className="checklist">
        {credentials.length > 0 ? (
          credentials.map((c) => (
            <li key={c.principalClass}>
              the <strong>{principalClassLabel(c.principalClass)}</strong> credential
              <span className="muted"> ({c.entitledServices.join(', ') || 'no entitlements'})</span>
            </li>
          ))
        ) : (
          <li>every license credential on this environment</li>
        )}
        <li>
          its row in the environments list
          <span className="muted"> (and its mapping to a customer, if any)</span>
        </li>
      </ul>

      <p className="muted">
        Migration-license grants and balances belong to the <strong>customer</strong>, not the
        environment, and are <strong>not</strong> deleted.
      </p>

      {/* The one thing the SERVER cannot check. Licensing is a leaf service and
          must not call MINT, so the device count is knowledge only this page
          has — it already fetched it for the Devices panel. Stated as a
          warning rather than a block: a test environment plausibly has test
          devices, and the rows left behind are inert (resolve validates the
          licence token before it reads the index, and the token is gone). */}
      {deviceCount !== null && deviceCount > 0 && (
        <div className="notice notice-warn">
          <strong>
            {deviceCount} device{deviceCount === 1 ? '' : 's'}
          </strong>{' '}
          {deviceCount === 1 ? 'is' : 'are'} still registered with MINT for this environment.
          Deleting here does not deregister {deviceCount === 1 ? 'it' : 'them'} — MINT is a
          separate service. {deviceCount === 1 ? 'The row' : 'Those rows'} will resolve to
          nothing.
        </div>
      )}
      {deviceCount === null && (
        <div className="notice notice-warn">
          The device list could not be read, so it is <strong>not known</strong> whether
          devices are registered with MINT for this environment.
        </div>
      )}

      {error && <Notice tone="error">{error}</Notice>}

      <label className="field-label" htmlFor="confirm-delete-label">
        Type <strong>{environmentLabel}</strong> to confirm
      </label>
      <input
        id="confirm-delete-label"
        type="text"
        value={typed}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => setTyped(e.target.value)}
      />

      <div className="modal-actions">
        <button onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button className="btn-danger" disabled={!confirmed || busy} onClick={onConfirm}>
          {busy ? 'Deleting…' : 'Delete environment'}
        </button>
      </div>
    </Modal>
  )
}
